// src/pages/skillsSections.js
//
// Pure, framework-free helpers for the skills-workshop reader: which lit_section
// kinds the skills reader shows (technique + analysis), the section selector, the
// unit grouping key (content.blocks.unit), and the two-tier nav split (Techniques /
// Past Papers). JSX-free so they unit-test under `node --test`; PackReaderSkills
// imports them for selection + grouping.

import { groupByDiscovered } from './readerGrouping.js';

export const SENTENCE_CRAFT_UNITS = ['Foundation', 'Developing', 'Advanced']
    .map(stage => 'Sentence Craft for Meaning & Control — ' + stage);

// Keep only the loaded stage groups together at their first visible position.
// No items are loaded or fabricated here; all other unit groups retain their order.
function sentenceCraftStageOrder(groups) {
    const stages = groups.filter(g => SENTENCE_CRAFT_UNITS.includes(g.label));
    if (stages.length < 2) return groups;
    stages.sort((a, b) => SENTENCE_CRAFT_UNITS.indexOf(a.label) - SENTENCE_CRAFT_UNITS.indexOf(b.label));
    const first = groups.findIndex(g => SENTENCE_CRAFT_UNITS.includes(g.label));
    return [...groups.slice(0, first), ...stages, ...groups.slice(first).filter(g => !SENTENCE_CRAFT_UNITS.includes(g.label))];
}

// The content types of the skills reader, in nav order, with their student-facing
// nav-section labels (matching the admin tab names). Single source of truth for both
// the section selector and the tiered nav split. Order (slice 3): Techniques, then
// Thematic Vocabulary (learning material), then Past Papers (mocks last).
const NAV_KINDS = [
    { kind: 'technique', label: 'Techniques' },
    { kind: 'vocabulary', label: 'Thematic Vocabulary' },
    { kind: 'analysis', label: 'Past Papers' },
];

export const SKILLS_SECTION_KINDS = NAV_KINDS.map((k) => k.kind);

// Skills reader content = lit_section items whose sectionKind is a skills kind.
export function selectSkillsSections(items) {
    return (items || []).filter((it) =>
        it && it.type === 'lit_section' && it.content && SKILLS_SECTION_KINDS.includes(it.content.sectionKind));
}

// The unit grouping key for a skills section (content.blocks.unit). Normalised the
// SAME way groupByDiscovered keys are, so a deep-link selects the right group.
export function unitKeyOf(item) {
    const u = item && item.content && item.content.blocks && item.content.blocks.unit;
    return (typeof u === 'string' && u.trim()) ? u.trim() : '';
}

// ---- Two-tier nav: content-type sections (Techniques / Past Papers) ----

// Which nav tier an item files under. 'analysis' and 'vocabulary' (slice 3) map to
// their own tiers; anything else (incl. unknowns) keeps the technique default so
// nothing ever vanishes from the nav.
const kindOf = (item) => {
    const k = item && item.content && item.content.sectionKind;
    return (k === 'analysis' || k === 'vocabulary') ? k : 'technique';
};

// Group key for an item under the two-tier nav. The sectionKind PREFIX keeps group
// keys unique across the two partitions: one unit string may legitimately hold both
// kinds (see skillsSections.test.js), and after partitioning that unit becomes a
// group in EACH nav-section -- unprefixed keys would collide in the reader's flat
// navGroups and break selection/deep-link.
export function skillsNavGroupKey(item) {
    return kindOf(item) + ':' + unitKeyOf(item);
}

// Ordered nav-sections for the skills sidebar: [{ key, label, groups }], where
// `groups` has the same [{ key, label, items }] shape buildGroups returns (so the
// reader renders them with the exact same group markup). Techniques first, then
// Past Papers; a partition with no sections is omitted entirely. Unit-less items
// fold into the partition's own fallback label. Each group's key is derived FROM
// ITS OWN ITEMS via skillsNavGroupKey (a group is never empty), so the deep-link
// contract -- skillsNavGroupKey(item) === key of the group containing it -- holds
// by construction.
export function buildSkillsNavSections(sections) {
    const all = sections || [];
    const out = [];
    for (const { kind, label } of NAV_KINDS) {
        const subset = all.filter((it) => it && kindOf(it) === kind);
        if (subset.length === 0) continue;
        const groups = groupByDiscovered(subset, unitKeyOf, { fallbackLabel: label })
            .map((g) => ({ ...g, key: skillsNavGroupKey(g.items[0]) }));
        out.push({ key: kind, label, groups: kind === 'technique' ? sentenceCraftStageOrder(groups) : groups });
    }
    return out;
}
